/**
 * Compact single-line chip for dense Month days — venue name + booked/capacity,
 * expanding to the full chip on hover (owned by WorkshopsCalendarChip).
 *
 * The NAME, truncated, not `venueShort`: three initials with no legend anywhere
 * on the page cannot be read, and the design shows full names here and lets
 * them ellipsis.
 */
import { isCancelled } from './cancelled-workshops';
import { DISABLED_LABEL, isDisabled } from './disabled-workshops';
import { PrivateEventBadge } from './PrivateEventBadge';
import type { ChipColors } from './workshops-calendar-chip-shared';
import { chipEdgeStyle, MONO, venueNameStyle } from './workshops-calendar-chip-shared';
import type { WorkshopChip } from './workshopsCalendarTypes';

export interface WorkshopsCalendarChipCompactProps {
  w: WorkshopChip;
  /** Venue-local clock label ("6:30p"), pre-formatted from `localDate`. */
  timeLabel: string;
  colors: ChipColors;
  clickable: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function WorkshopsCalendarChipCompact({
  w,
  timeLabel,
  colors,
  clickable,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: WorkshopsCalendarChipCompactProps) {
  const state = isCancelled(w) ? 'Cancelled · ' : isDisabled(w) ? `${DISABLED_LABEL} · ` : '';
  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      title={`${state}${w.venueName} · ${w.species ?? ''} · ${timeLabel}${w.leadInstructor ? ' · ' + w.leadInstructor : ''}${w.assistant ? ' + ' + w.assistant : ''}`}
      style={{
        marginTop: 2,
        padding: '2px 5px',
        background: colors.bg,
        borderLeft: `3px ${chipEdgeStyle(w)} ${colors.border}`,
        borderRadius: 3,
        boxShadow: colors.shadow,
        cursor: clickable ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'baseline',
        gap: 4,
        minWidth: 0,
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 10,
          fontWeight: 600,
          color: 'var(--nb-ink)',
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...venueNameStyle(w),
        }}
      >
        {w.venueName || w.venueShort}
      </span>
      {w.isPrivate && <PrivateEventBadge fontSize={7.5} />}
      <span
        style={{
          ...MONO,
          fontSize: 8.5,
          fontWeight: 600,
          color: 'var(--nb-subtle)',
          flexShrink: 0,
        }}
      >
        {w.booked}/{w.capacity}
      </span>
    </div>
  );
}
